/**
 * ForceChangePassword - Blocking screen shown when the current user has
 * `mustChangePassword=true`. The screen disallows interacting with the rest
 * of the app until a new password is set.
 */
import { useState } from "react";

import { AuthClient } from "./AuthClient";
import { AuthField, AuthFields } from "./components/AuthField";
import { AuthError, AuthScreen } from "./components/AuthScreen";
import { AuthLinkButton, AuthSubmitButton } from "./components/AuthSubmitButton";
import { useAuthStore } from "./authStore";

const ERROR_ID = "force-change-password-error";

export function ForceChangePassword() {
  const user = useAuthStore((state) => state.user);
  const hydrate = useAuthStore((state) => state.hydrate);
  const logout = useAuthStore((state) => state.logout);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local validation errors (min length, mismatch, same-as-current) all point
  // at the new password: it is the field the user needs to retype in each
  // case. A server-side failure (wrong current password) isn't attributable
  // to any one field, so nothing is marked invalid for it.
  const [invalidField, setInvalidField] = useState<"newPassword" | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setInvalidField(null);
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      setInvalidField("newPassword");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      setInvalidField("newPassword");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from the current one.");
      setInvalidField("newPassword");
      return;
    }
    setSubmitting(true);
    try {
      await AuthClient.changePassword(currentPassword, newPassword);
      await hydrate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthScreen
      layer="overlay"
      title="Choose a new password"
      subtitle={
        user?.email
          ? `Welcome ${user.name}. You must change the temporary password before continuing.`
          : "You must change the temporary password before continuing."
      }
      onSubmit={handleSubmit}
      footer={
        <AuthLinkButton
          onClick={() => {
            void logout().finally(() => {
              window.location.assign("/login");
            });
          }}
        >
          Sign out instead
        </AuthLinkButton>
      }
    >
      <AuthFields>
        <AuthField
          id="current-password"
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
        <AuthField
          id="new-password"
          label="New password (min 8 chars)"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          minLength={8}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          aria-invalid={invalidField === "newPassword"}
          aria-describedby={invalidField === "newPassword" ? ERROR_ID : undefined}
        />
        <AuthField
          id="confirm-password"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          minLength={8}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
      </AuthFields>

      {error ? <AuthError id={ERROR_ID}>{error}</AuthError> : null}

      <AuthSubmitButton pending={submitting}>
        {submitting ? "Updating…" : "Update password"}
      </AuthSubmitButton>
    </AuthScreen>
  );
}
